/**
 * SmartNavbar - Context-aware navigation that preserves user session
 * Passes tokens and context when navigating between pages
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarContext {
  token?: string | null;
  profileId?: string | null;
  moonSign?: string | null;
  lunaSessionId?: string | null;
}

const SmartNavbar: React.FC = () => {
  const location = useLocation();
  const [context, setContext] = useState<NavbarContext>({});

  useEffect(() => {
    // Extract context from current URL
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const lunaSession = params.get('luna_session');

    // Also check localStorage for cached profile
    const cachedProfile = localStorage.getItem('userProfile');
    let profileData = null;
    if (cachedProfile) {
      try {
        profileData = JSON.parse(cachedProfile);
      } catch (e) {
        console.error('Failed to parse cached profile:', e);
      }
    }

    // Check sessionStorage for temporary moon reading data
    const tempMoonReading = sessionStorage.getItem('tempMoonReadingData');
    let tempData = null;
    if (tempMoonReading) {
      try {
        tempData = JSON.parse(tempMoonReading);
      } catch (e) {
        console.error('Failed to parse temp moon reading:', e);
      }
    }

    setContext({
      token: token || tempData?.token || null,
      profileId: profileData?.id || tempData?.profileId || null,
      moonSign: profileData?.moon_sign || tempData?.moonSign || null,
      lunaSessionId: lunaSession || tempData?.lunaSessionId || null
    });
  }, [location]);

  // Helper to build context-aware links
  const buildContextLink = (path: string): string => {
    // For the app page, pass context parameters
    if (path === '/app' && (context.token || context.lunaSessionId)) {
      const params = new URLSearchParams();
      if (context.token) params.set('token', context.token);
      if (context.lunaSessionId) params.set('luna_session', context.lunaSessionId);
      return `${path}?${params.toString()}`;
    }
    return path;
  };

  return (
    <nav className="bg-forest-darkest/80 backdrop-blur-md py-4 px-6 flex flex-col md:flex-row justify-between items-center sticky top-0 z-50 border-b border-antique-gold/20">
      <div className="flex items-center mb-4 md:mb-0">
        <Link to="/" className="flex items-center">
          <div className="w-10 h-10 rounded-full bg-antique-gold flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-forest-darkest flex items-center justify-center">
              <span className="text-antique-gold">☽</span>
            </div>
          </div>
          <span className="ml-2 text-xl font-bold">MoonManifest</span>
        </Link>
      </div>
      <div className="flex flex-wrap justify-center space-x-3 md:space-x-6">
        <Link to="/" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Home</Link>
        <Link to="/free-moon-reading" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Free Moon Reading</Link>
        <Link
          to={buildContextLink('/app')}
          className="hover:text-antique-gold transition-colors py-2 font-semibold text-antique-gold"
        >
          Try App
        </Link>
        <Link to="/moon-signs" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Moon Signs</Link>
        <Link to="/moon-phases" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Moon Phases</Link>
        <Link to="/manifestation" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Manifestation</Link>
        <Link to="/tools/calculators/moon-sign-calculator" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Calculator</Link>
        <Link to="/tools/quizzes/manifestation-readiness-quiz" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Quiz</Link>
        <Link to="/blog" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Blog</Link>
        <Link to="/contact" className="hover:text-antique-gold transition-colors py-2 text-manuscript-cream/90">Contact</Link>

      </div>
    </nav>
  );
};

export default SmartNavbar;